import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { FiCheck, FiHeart, FiShoppingBag } from "react-icons/fi";
import siteConfig from "../config/siteConfig";
import { useCart } from "./CartContext";
import "../css/productcard.scss";

const currencyFormatter = new Intl.NumberFormat(siteConfig.locale, {
  style: "currency",
  currency: siteConfig.currency,
});

const getProductInitials = (name) =>
  name
    .split(" ")
    .slice(0, 2)
    .map((word) => word.charAt(0))
    .join("");

function ProductCard({ compact = false, product }) {
  const { addToCart, isWishlisted, toggleWishlist } = useCart();
  const [addCount, setAddCount] = useState(0);
  const [imageFailed, setImageFailed] = useState(false);
  const wishlisted = isWishlisted(product.id);
  const detailsPath = `/products/${product.slug}`;
  const isSoldOut = Number(product.stock) <= 0;
  const primaryImage = product.image || product.images?.[0];

  useEffect(() => {
    if (!addCount) return undefined;

    const timer = window.setTimeout(() => setAddCount(0), 1600);
    return () => window.clearTimeout(timer);
  }, [addCount]);

  const handleQuickAdd = () => {
    addToCart(product.id, 1);
    setAddCount((count) => count + 1);
  };

  return (
    <article className={`product-card${compact ? " product-card--compact" : ""}`}>
      <div className="product-card__media">
        <Link aria-label={`View ${product.name}`} to={detailsPath}>
          {imageFailed || !primaryImage ? (
            <span className="product-card__image-fallback" role="img" aria-label={product.name}>
              {getProductInitials(product.name)}
            </span>
          ) : (
            <img
              alt={product.name}
              loading="lazy"
              onError={() => setImageFailed(true)}
              src={primaryImage}
            />
          )}
        </Link>

        {product.badge && <span className="product-card__badge">{product.badge}</span>}

        <button
          aria-label={`${wishlisted ? "Remove" : "Add"} ${product.name} ${
            wishlisted ? "from" : "to"
          } wishlist`}
          aria-pressed={wishlisted}
          className={`product-card__wishlist${wishlisted ? " is-active" : ""}`}
          onClick={() => toggleWishlist(product.id)}
          type="button"
        >
          <FiHeart aria-hidden="true" />
        </button>
      </div>

      <div className="product-card__body">
        <div className="product-card__meta">
          <span>{product.serves ? `Serves ${product.serves}` : product.weight}</span>
          {product.newArrival && <span>New arrival</span>}
        </div>
        <p className="product-card__brand">{siteConfig.brandName}</p>
        <h2>
          <Link to={detailsPath}>{product.name}</Link>
        </h2>
        <p className="product-card__description">{product.description}</p>

        <div className="product-card__purchase">
          <p className="product-card__price">{currencyFormatter.format(product.price)}</p>

            <button
              className={`product-card__add${addCount ? " is-added" : ""}`}
              disabled={isSoldOut}
              onClick={handleQuickAdd}
              type="button"
            >
              {isSoldOut ? (
                "Sold out"
              ) : addCount ? (
                <>
                  <FiCheck aria-hidden="true" /> Added
                </>
              ) : (
                <>
                  <FiShoppingBag aria-hidden="true" /> Add to cart
                </>
              )}
            </button>
        </div>
        <span className="product-card__status" aria-live="polite">
          {addCount ? `${product.name} added to cart.` : ""}
        </span>
      </div>
    </article>
  );
}

export default ProductCard;
